import { logger } from '@/lib/logger';

// Rate limiting for search operations
class SearchRateLimiter {
  private requests: Map<string, number[]> = new Map();
  private readonly maxRequests = 10; // Max requests per window
  private readonly windowMs = 60000; // 1 minute window

  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const userRequests = this.requests.get(identifier) || [];
    
    // Remove old requests outside the window
    const validRequests = userRequests.filter(time => now - time < this.windowMs);
    
    if (validRequests.length >= this.maxRequests) {
      logger.warn('[SearchRateLimiter] Rate limit exceeded:', { identifier, requests: validRequests.length });
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    
    return true;
  }
}

export const searchRateLimiter = new SearchRateLimiter();

// Sanitize search queries
export const sanitizeSearchQuery = (query: string): string => {
  if (!query || typeof query !== 'string') {
    return '';
  }
  
  // Trim whitespace and limit length
  const sanitized = query.trim().substring(0, 1000);
  
  // Remove potentially dangerous characters
  const cleaned = sanitized.replace(/[<>\"'&\\]/g, '');
  
  // Log suspicious patterns
  if (sanitized !== cleaned) {
    logger.warn('[InputSanitization] Suspicious characters removed from search query');
  }
  
  return cleaned;
};

// Sanitize category filter
export const sanitizeCategoryFilter = (category: string): string => {
  if (!category || typeof category !== 'string') {
    return '';
  }
  
  // Categories should be alphanumeric with spaces, hyphens, underscores
  const sanitized = category.trim().substring(0, 100);
  const cleaned = sanitized.replace(/[^a-zA-Z0-9\s\-_]/g, '');
  
  if (sanitized !== cleaned) {
    logger.warn('[InputSanitization] Invalid characters removed from category filter');
  }
  
  return cleaned;
};

// Validate and sanitize form inputs
export const sanitizeItemInput = (input: any): any => {
  if (!input || typeof input !== 'object') {
    return {};
  }

  const sanitized = { ...input };

  // Sanitize string fields
  if (typeof sanitized.name === 'string') {
    sanitized.name = sanitized.name.trim().substring(0, 255);
  }
  
  if (typeof sanitized.category === 'string') {
    sanitized.category = sanitizeCategoryFilter(sanitized.category);
  }
  
  if (typeof sanitized.notes === 'string') {
    sanitized.notes = sanitized.notes.trim().substring(0, 2000);
  }

  // Validate numeric fields
  ['shopResourceLoad', 'specsResourceLoad', 'poResourceLoad'].forEach(field => {
    if (sanitized[field] !== undefined) {
      const num = Number(sanitized[field]);
      sanitized[field] = isNaN(num) || num < 1 || num > 10 ? 1 : Math.floor(num);
    }
  });

  return sanitized;
};

// Enhanced logging for suspicious activity
export const logSuspiciousActivity = (activity: string, details: any) => {
  logger.warn('[SecurityAlert]', activity, details);
  
  // In production, you might want to send this to a security monitoring service
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    // Could integrate with security monitoring tools here
  }
};